import type { ColumnHelper } from '@tanstack/react-table';
import { type TFunction } from 'i18next';

import {
  ArrowExpandIcon,
  DefaultInfluencerIcon,
  MoneyIcon,
} from '../../../assets';
import type {
  CampaignInvoicesTable,
  CampaignUserInvoiceStatusType,
} from '../../../types';
import { StatusText } from '../../common/StatusText';
import { BrandImage } from '../../images/BrandImage';
import { UsersImages } from '../../images/UserImages';
import DTBoldCell from '../DTBoldCell';
import { DTCell } from '../DTCell';
import DTHeader from '../DTHeader';

type GetCampaignsTableHeadersProps = {
  columnHelper: ColumnHelper<CampaignInvoicesTable>;
  handleChangeSortDirection: (id: string) => void;
  t: TFunction;
  extras: ExtraInfoCampaignHeader;
};

export type ExtraInfoCampaignHeader = {
  isInfluencer: boolean;
};

export const getCampaignInvoicesTableHeaders = ({
  columnHelper,
  handleChangeSortDirection,
  t,
  extras: { isInfluencer },
}: GetCampaignsTableHeadersProps) =>
  [
    columnHelper.accessor('name', {
      size: 150,
      minSize: 150,
      maxSize: 200,
      cell: (info) => {
        if (!info.row.getCanExpand() || isInfluencer)
          return <DTBoldCell content={info.getValue()} id={info.cell.id} />;
        return (
          <div>
            <div className="flex flex-row">
              <button
                className={`flex items-center justify-center ${info.row.getIsExpanded() ? 'rotate-90' : ''}`}
                {...{
                  onClick: (e) => {
                    e.stopPropagation();
                    const expand = info.row.getToggleExpandedHandler();
                    expand();
                  },
                  style: { cursor: 'pointer' },
                }}
              >
                <ArrowExpandIcon />
              </button>
              <DTBoldCell content={info.getValue()} id={info.cell.id} />
            </div>
          </div>
        );
      },
      header: ({ header, ...info }) => {
        return (
          <DTHeader
            sortable
            first
            onClick={() => handleChangeSortDirection(header.id)}
            title={t('invoices.header1')}
            sorting={(info as any).orderBy}
            sortingDirection={(info as any).orderDirection}
            accessor="name"
          />
        );
      },
    }),
    columnHelper.accessor('brand.name', {
      size: 150,
      minSize: 150,
      maxSize: 200,
      cell: (info) => {
        return (
          <div className="flex w-full justify-start flex-row gap-x-2 items-center">
            <BrandImage
              size="size-[50px]"
              image={info.row.original.brand.profile_picture}
            />
            <p>{info.getValue() as string}</p>
          </div>
        );
      },
      header: ({ header }) => (
        <DTHeader
          onClick={() => handleChangeSortDirection(header.id)}
          title={t('invoices.header2')}
          accessor={'brand_name'}
        />
      ),
    }),
    !isInfluencer
      ? columnHelper.accessor('users', {
          size: 150,
          minSize: 150,
          maxSize: 200,
          cell: (info) => {
            const val = info.getValue() as { profile_picture: string }[];
            return (
              <UsersImages
                size="size-[35px]"
                userCount={val?.length || 0}
                userImages={val.map((v) => v.profile_picture) || []}
              />
            );
          },
          header: ({ header }) => (
            <DTHeader
              onClick={() => handleChangeSortDirection(header.id)}
              title={t('invoices.header1-brand')}
              accessor={'influencers'}
            />
          ),
        })
      : undefined,
    columnHelper.accessor('price', {
      size: 150,
      minSize: 150,
      maxSize: 200,
      header: ({ header, ...info }) => (
        <DTHeader
          title={t('invoices.header3')}
          sorting={(info as any).orderBy}
          sortingDirection={(info as any).orderDirection}
          accessor="price"
        />
      ),
      cell: (info) => {
        return <DTCell text={info.getValue()} icon={<MoneyIcon />} />;
      },
    }),
    columnHelper.accessor('status', {
      size: 150,
      minSize: 150,
      maxSize: 200,
      header: ({ header, ...info }) => (
        <DTHeader
          title={t('invoices.header4')}
          sorting={(info as any).orderBy}
          sortingDirection={(info as any).orderDirection}
          accessor="status"
        />
      ),
      cell: (info) => {
        return <StatusText type={'brandInvoice'} status={info.getValue()} />;
      },
    }),
    columnHelper.accessor('billing_date', {
      size: 150,
      minSize: 150,
      maxSize: 200,
      header: ({ header, ...info }) => (
        <DTHeader
          title={t('invoices.header5')}
          sorting={(info as any).orderBy}
          sortingDirection={(info as any).orderDirection}
          accessor="billing_date"
        />
      ),
      cell: (info) => {
        return <DTCell text={info.getValue()} />;
      },
    }),
    columnHelper.accessor('payment_date', {
      size: 150,
      minSize: 150,
      maxSize: 200,
      header: ({ header, ...info }) => (
        <DTHeader
          title={t('invoices.header6')}
          sorting={(info as any).orderBy}
          sortingDirection={(info as any).orderDirection}
          accessor="payment_date"
        />
      ),
      cell: (info) => {
        return <DTCell text={info.getValue()} />;
      },
    }),
  ].filter((col) => col !== undefined) as unknown as Array<
    ReturnType<ColumnHelper<CampaignInvoicesTable>['accessor']>
  >;

export const CAMPAIGN_INVOICE_TABLE_SUBROWS = {
  influencer: {
    size: 150,
    minSize: 150,
    maxSize: 200,
    cell: (val: string, id: string, image: string) => {
      return (
        <div className="flex flex-row gap-x-4 items-center">
          {image ? (
            <img
              src={`${process.env.REACT_APP_AWS_PUBLIC_ENDPOINT}/${image}`}
              className={`rounded-full size-[50px] object-cover`}
            />
          ) : (
            <div className={`rounded-full bg-influentia-black size-[50px]`}>
              <DefaultInfluencerIcon className="size-[inherit]" />
            </div>
          )}
          <DTBoldCell content={val} id={id} />
        </div>
      );
    },
  },
  price: {
    size: 150,
    minSize: 150,
    maxSize: 200,
    cell: (val: string) => {
      return <DTCell text={val || '-'} icon={<MoneyIcon />} />;
    },
  },
  status: {
    size: 150,
    minSize: 150,
    maxSize: 200,
    cell: (val: CampaignUserInvoiceStatusType) => {
      return <StatusText type="userInvoice" status={val} />;
    },
  },
  billing_date: {
    size: 150,
    minSize: 150,
    maxSize: 200,
    cell: (val: string) => {
      return <DTCell text={val || '-'} />;
    },
  },
  payment_date: {
    size: 150,
    minSize: 150,
    maxSize: 200,
    cell: (val: string) => {
      return <DTCell text={val || '-'} />;
    },
  },
};
